import {
  Component,
  ChangeDetectionStrategy,
  input,
  computed,
  signal,
  output,
  inject,
  OnInit,
  DestroyRef,
} from '@angular/core';

export type QuotesViewState =
  | 'detail'
  | 'progress'
  | 'final'
  | 'rejected'
  | 'pending'
  | 'cancelledPending'
  | 'cancelledReviewed'
  | 'cancelledExpired'
  | 'reviewed'
  | 'notAvailable'
  | 'fallback'
  | 'empty';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import {
  REJECTED_REQUEST_SUPPORT_EMAIL,
  REJECTED_REQUEST_SUPPORT_SUBJECT,
} from '../../../../../shared/config/constants';
import {
  Estimate,
  VendorOrder,
} from '../../../../../shared/models/vendor-order.model';
import {
  isRejectedEstimate,
  isEstimateApproved,
  isCancelledFromPending as isCancelledFromPendingUtil,
  isCancelledFromReviewed as isCancelledFromReviewedUtil,
  isCancelledFromExpired as isCancelledFromExpiredUtil,
} from '../cancellation-status.util';
import { QuoteCardComponent } from './quote-card/quote-card.component';
import { QuoteDetailsComponent } from './quote-details/quote-details.component';
import { QuoteProgressComponent } from './quote-progress/quote-progress.component';
import { QuoteFinalComponent } from './quote-final/quote-final.component';
import { VendorRejectionsAccordionComponent } from './vendor-rejections-accordion/vendor-rejections-accordion.component';
import { CancelledEstimatesListComponent } from './cancelled-estimates-list/cancelled-estimates-list.component';
import { ExpiredQuotesAccordionComponent } from './expired-quotes-accordion/expired-quotes-accordion.component';
import { VendorOrdersService } from '../services/vendor-orders.service';

const FINAL_STATUSES = new Set([
  'IN_PROGRESS',
  'COMPLETED',
  'AWAITING_AUTHORIZATION',
]);
const PROGRESS_VIEW_STATUSES = new Set([
  'IN_PROGRESS',
  'AWAITING_DTP_PROCESS',
  'COMPLETED',
]);
const QUOTE_PENDING_STATUSES = new Set([
  'AWAITING_ESTIMATION',
  'AWAITING_VENDOR_APPROVAL',
]);
const QUOTES_REVIEWED_STATUSES = new Set([
  'APPROVED',
  'ON_HOLD',
  'AWAITING_VENDOR_SELECTION',
  'AWAITING_PURCHASE_ORDER',
]);

@Component({
  selector: 'app-quotes',
  imports: [
    QuoteCardComponent,
    QuoteDetailsComponent,
    QuoteProgressComponent,
    QuoteFinalComponent,
    VendorRejectionsAccordionComponent,
    CancelledEstimatesListComponent,
    ExpiredQuotesAccordionComponent,
    MatIconModule,
  ],
  templateUrl: './quotes.component.html',
  styleUrl: './quotes.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuotesComponent implements OnInit {
  private readonly vendorOrdersService = inject(VendorOrdersService);
  private readonly destroyRef = inject(DestroyRef);

  readonly order = input<VendorOrder>();
  readonly selectedEstimate = signal<Estimate | null>(null);
  readonly quoteConfirmed = output<string>();
  readonly cancelledEstimates = signal<Estimate[]>([]);
  readonly cancelledNoResponseMessage =
    'The vendor did not submit a quote before the request was canceled by the user.';

  readonly allEstimates = computed(() => this.sortedEstimates());
  readonly estimates = computed(() => this.order()?.estimates ?? []);

  readonly pendingEstimates = computed(() =>
    this.allEstimates().filter(
      (estimate) =>
        estimate.jobStatus !== 'CANCELLED' &&
        !this.isRejectedEstimate(estimate),
    ),
  );
  readonly orderDeadline = computed(
    () => this.order()?.deadline ?? this.order()?.fixedDeadline,
  );
  readonly hasAnyEstimates = computed(() => this.estimates().length > 0);

  readonly isQuotePending = computed(() => {
    const status = this.order()?.jobStatus;
    return !!status && QUOTE_PENDING_STATUSES.has(status);
  });

  readonly isQuotesReviewed = computed(() => {
    const status = this.order()?.jobStatus;
    return !!status && QUOTES_REVIEWED_STATUSES.has(status);
  });

  readonly isRejectedRequest = computed(
    () => this.order()?.jobStatus === 'REJECTED',
  );
  readonly isCancelledRequest = computed(
    () => this.order()?.jobStatus === 'CANCELLED',
  );

  readonly rejectedEstimates = computed(() =>
    this.allEstimates().filter((estimate) => this.isRejectedEstimate(estimate)),
  );

  readonly isPartialRejectionScenario = computed(
    () => !this.isRejectedRequest() && this.rejectedEstimates().length > 0,
  );
  readonly rejectedVendorCount = computed(
    () => this.rejectedEstimates().length,
  );
  readonly rejectedSupportHref = computed(
    () =>
      `mailto:${REJECTED_REQUEST_SUPPORT_EMAIL}?subject=${encodeURIComponent(REJECTED_REQUEST_SUPPORT_SUBJECT)}`,
  );

  readonly estimatesWithQuotes = computed(() =>
    this.allEstimates().filter(
      (estimate) =>
        !!estimate.quote &&
        estimate.jobStatus !== 'CANCELLED' &&
        !this.isRejectedEstimate(estimate),
    ),
  );

  readonly estimatesWithoutQuotes = computed(() =>
    this.allEstimates().filter(
      (estimate) =>
        !estimate.quote &&
        estimate.jobStatus !== 'CANCELLED' &&
        !this.isRejectedEstimate(estimate),
    ),
  );

  readonly shouldShowInfoMessage = computed(
    () => this.isQuotesReviewed() && this.estimatesWithQuotes().length > 0,
  );

  readonly cancelledApprovedEstimates = computed(() =>
    this.allEstimates().filter(
      (estimate) =>
        !this.isRejectedEstimate(estimate) && this.isEstimateApproved(estimate),
    ),
  );

  readonly cancelledUnrespondedEstimates = computed(() =>
    this.allEstimates().filter(
      (estimate) =>
        !this.isRejectedEstimate(estimate) &&
        !this.isEstimateApproved(estimate),
    ),
  );

  readonly cancelledRejectedEstimates = computed(() =>
    this.allEstimates().filter((estimate) => this.isRejectedEstimate(estimate)),
  );

  readonly isCancelledFromPending = computed(() =>
    isCancelledFromPendingUtil(this.order()),
  );

  readonly isCancelledFromReviewed = computed(() =>
    isCancelledFromReviewedUtil(this.order()),
  );

  readonly isCancelledFromExpired = computed(() =>
    isCancelledFromExpiredUtil(this.order()),
  );

  readonly isInProgress = computed(() => {
    const status = this.order()?.jobStatus;
    return !!status && PROGRESS_VIEW_STATUSES.has(status);
  });

  readonly isFinalQuoteView = computed(() => {
    const o = this.order();
    return !!o?.jobStatus && FINAL_STATUSES.has(o.jobStatus);
  });

  readonly selectedVendorEstimate = computed<Estimate | null>(() => {
    const o = this.order();
    if (!o?.vendorId || !this.isFinalQuoteView()) return null;
    return (
      this.estimates().find(
        (e) => (e.vendorId ?? e.workflow_uid) === o.vendorId,
      ) ?? null
    );
  });

  readonly cancelledStatusEstimates = computed(() => {
    const files = this.order()?.files ?? [];
    return this.allEstimates()
      .filter((estimate) => estimate.jobStatus === 'CANCELLED')
      .map((estimate) => {
        const vendorFile = files.find((f) => f.vendorName === estimate.name);
        return {
          ...estimate,
          cancellationReason: vendorFile?.cancellationReason ?? null,
        };
      });
  });

  /**
   * Determines which view the Quotes tab renders. Evaluated top-to-bottom; first match wins.
   *
   * 'detail'            → User clicked "View Details" on a quote card (selectedEstimate is set).
   * 'progress'          → jobStatus ∈ {IN_PROGRESS, AWAITING_DTP_PROCESS, COMPLETED}.
   * 'final'             → jobStatus ∈ {IN_PROGRESS, COMPLETED, AWAITING_AUTHORIZATION}
   *                       AND order.vendorId matches an estimate (selected vendor found).
   * 'rejected'          → jobStatus === 'REJECTED' (all vendors rejected the request).
   * 'pending'           → jobStatus ∈ {AWAITING_ESTIMATION, AWAITING_VENDOR_APPROVAL}
   *                       (vendors have been contacted but no quotes submitted yet).
   * 'cancelledPending'  → jobStatus === 'CANCELLED' AND no estimate was approved or rejected
   *                       (job cancelled before any vendor responded).
   * 'cancelledReviewed' → jobStatus === 'CANCELLED' AND at least one estimate was
   *                       previously approved or rejected (job cancelled after review began).
   * 'reviewed'          → jobStatus ∈ {APPROVED, ON_HOLD, AWAITING_VENDOR_SELECTION,
   *                       AWAITING_PURCHASE_ORDER} (quotes received and under review).
   * 'fallback'          → None of the above matched but estimates[] is non-empty.
   * 'notAvailable'      → jobStatus === 'NOT_AVAILABLE' (no suppliers cover the languages).
   * 'empty'             → No estimates and no special status — default blank state.
   */
  readonly viewState = computed<QuotesViewState>(() => {
    if (this.selectedEstimate()) return 'detail';
    if (this.isInProgress()) return 'progress';
    if (this.isFinalQuoteView() && this.selectedVendorEstimate())
      return 'final';
    if (this.isRejectedRequest()) return 'rejected';
    if (this.isQuotePending()) return 'pending';
    if (this.isCancelledFromExpired()) return 'cancelledExpired';
    if (this.isCancelledFromPending()) return 'cancelledPending';
    if (this.isCancelledFromReviewed()) return 'cancelledReviewed';
    if (this.isQuotesReviewed()) return 'reviewed';
    if (this.estimates().length) return 'fallback';
    if (this.order()?.jobStatus === 'NOT_AVAILABLE') return 'notAvailable';
    return 'empty';
  });

  readonly lowestOverallId = computed<string | null>(() => {
    const cancelled = this.cancelledEstimates();
    const estimates = cancelled.length ? cancelled : this.estimates();
    if (!estimates.length) return null;

    let lowestCost = Infinity;
    let lowestId: string | null = null;

    for (const est of estimates) {
      const cost = est.quote?.normal?.totalCost ?? est.priceMax ?? est.priceMin;
      if (cost == null) continue;
      const id = est.vendorId ?? est.estimationsId ?? null;
      if (id == null) continue;
      if (cost < lowestCost) {
        lowestCost = cost;
        lowestId = id;
      }
    }

    return lowestId;
  });

  ngOnInit(): void {
    const o = this.order();
    const jobUid = o?.jobUid;
    if (jobUid && (this.isInProgress() || this.isFinalQuoteView())) {
      this.vendorOrdersService
        .getCancelledEstimations(jobUid)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (estimates) => this.cancelledEstimates.set(estimates),
        });
    }
  }

  onViewDetails(estimate: Estimate): void {
    this.selectedEstimate.set(estimate);
  }

  onBack(): void {
    this.selectedEstimate.set(null);
  }

  isLowestRate(estimate: Estimate): boolean {
    const cost =
      estimate.quote?.normal?.totalCost ??
      estimate.priceMax ??
      estimate.priceMin;
    if (!cost) return false;
    const id = estimate.vendorId ?? estimate.estimationsId ?? null;
    if (id == null) return false;
    return id === this.lowestOverallId();
  }

  private isRejectedEstimate(estimate: Estimate): boolean {
    return isRejectedEstimate(estimate);
  }

  private isEstimateApproved(estimate: Estimate): boolean {
    return isEstimateApproved(estimate);
  }

  private readonly sortedEstimates = computed(() => {
    const estimates = [...this.estimates()];
    estimates.sort((a, b) => {
      const costA = a.quote
        ? (a.quote.normal?.totalCost ?? Infinity)
        : (a.priceMax ?? Infinity);
      const costB = b.quote
        ? (b.quote.normal?.totalCost ?? Infinity)
        : (b.priceMax ?? Infinity);
      return costA - costB;
    });
    return estimates;
  });
}
